/**
 * WhyAffordance — §19 transparency rule, calm + optional.
 *
 * One small text trigger ("Why am I seeing this?") that, when
 * tapped, reveals a single sentence of plain-English explanation —
 * the `reason` string the backend supplied for this card.
 *
 * Doctrine rules baked in:
 *   • The trigger is QUIET — not a button, not coloured, not iconed.
 *     Per §17 + the user's Step 6 directive: "no noisy UI".
 *   • The shown text is the BACKEND'S `reason` field, verbatim.
 *     The frontend never rewrites it (§19 "frontend MUST NOT
 *     rewrite `reason` client-side").
 *   • If `reason` is empty or missing, the trigger never renders.
 *     Silence is the calm baseline (§18).
 *
 * §23 Step 7 (May 2026) — open-state memory:
 *   When the same `signal_key` is shown again after a /home refresh
 *   (e.g. the 60-second poll re-computes and re-issues the same
 *   spine signal), a learner who already opened "Why?" stays in
 *   the same state. Memory is per-tab (sessionStorage) and capped
 *   to the most recent open keys to keep storage tiny.
 *
 *   No new UI. No new tracking. Storage failure is silent.
 */
import { useEffect, useState } from "react";

const MEMORY_KEY = "learner_state_why_open_v1";
const MEMORY_CAP = 64;            // max signal_keys remembered

function readMemory() {
  try {
    const raw = sessionStorage.getItem(MEMORY_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeMemory(set) {
  try {
    // Keep the latest MEMORY_CAP entries — drop the oldest if
    // the set ever exceeds the cap.
    const arr = Array.from(set);
    const trimmed = arr.length > MEMORY_CAP
      ? arr.slice(arr.length - MEMORY_CAP)
      : arr;
    sessionStorage.setItem(MEMORY_KEY, JSON.stringify(trimmed));
  } catch {
    // Private mode / quota — silent.
  }
}

export default function WhyAffordance({ reason, signalKey, testid }) {
  const memoryKey = signalKey || null;
  const [open, setOpen] = useState(() => {
    if (!memoryKey) return false;
    return readMemory().has(memoryKey);
  });

  // Sync state into sessionStorage whenever the user toggles or
  // a new signal_key first surfaces with a remembered state.
  useEffect(() => {
    if (!memoryKey) return;
    const mem = readMemory();
    const has = mem.has(memoryKey);
    if (open && !has) {
      mem.add(memoryKey);
      writeMemory(mem);
    } else if (!open && has) {
      mem.delete(memoryKey);
      writeMemory(mem);
    }
  }, [open, memoryKey]);

  const text = (reason || "").trim();
  if (!text) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid || "slot-why-trigger"}
        className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors underline decoration-dotted underline-offset-2"
      >
        {open ? "Hide" : "Why am I seeing this?"}
      </button>
      {open && (
        <p
          data-testid={`${testid || "slot-why"}-text`}
          className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed"
        >
          {text}
        </p>
      )}
    </div>
  );
}
